"use client";

import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

type Orientation = "portrait" | "landscape";

interface DeviceContextType {
  isMobile: boolean;
  isTablet: boolean;
  isAndroid: boolean;
  isIOS: boolean;
  orientation: Orientation;
  width: number;
  height: number;
}

const DeviceContext = createContext<DeviceContextType | undefined>(undefined);

const DEFAULT_DEVICE_INFO: DeviceContextType = {
  isMobile: false,
  isTablet: false,
  isAndroid: false,
  isIOS: false,
  orientation: "portrait",
  width: 0,
  height: 0,
};

// Hàm kiểm tra thiết bị mobile
const checkIsMobile = (): boolean => {
  if (typeof window === "undefined") return false;

  const userAgent = navigator.userAgent || navigator.vendor;

  // Kiểm tra các pattern phổ biến của mobile
  const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;

  // Kiểm tra kích thước màn hình
  const isSmallScreen = window.innerWidth <= 768;

  // Kiểm tra touch support
  const hasTouchScreen = "ontouchstart" in window || navigator.maxTouchPoints > 0;

  return mobileRegex.test(userAgent) || (isSmallScreen && hasTouchScreen);
};

const checkIsTablet = (): boolean => {
  if (typeof window === "undefined") return false;

  return window.innerWidth >= 768 && window.innerWidth <= 1024;
};

const checkIsAndroid = (): boolean => {
  if (typeof window === "undefined") return false;
  return /android/i.test(navigator.userAgent);
};

const checkIsIOS = (): boolean => {
  if (typeof window === "undefined") return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
};

// Hàm lấy orientation
const getOrientation = (): Orientation => {
  if (typeof window === "undefined") return "portrait";

  return window.innerWidth > window.innerHeight ? "landscape" : "portrait";
};

interface DeviceProviderProps {
  children: React.ReactNode;
}

export const DeviceProvider: React.FC<DeviceProviderProps> = ({ children }) => {
  // hydration mismatch 방지:
  // SSR과 CSR의 첫 렌더를 동일하게 유지하고, mounted 이후에 실제 값으로 갱신한다.
  const [deviceInfo, setDeviceInfo] = useState<DeviceContextType>(DEFAULT_DEVICE_INFO);

  const updateDeviceInfo = useCallback(() => {
    if (typeof window === "undefined") return;

    setDeviceInfo({
      isMobile: checkIsMobile(),
      isTablet: checkIsTablet(),
      isAndroid: checkIsAndroid(),
      isIOS: checkIsIOS(),
      orientation: getOrientation(),
      width: window.innerWidth,
      height: window.innerHeight,
    });
  }, []);

  useEffect(() => {
    // mounted 직후 1회 갱신 (queueMicrotask으로 defer → setState trong callback, không sync trong effect)
    queueMicrotask(updateDeviceInfo);

    // Lắng nghe sự kiện resize
    window.addEventListener("resize", updateDeviceInfo);

    // Lắng nghe sự kiện orientationchange (cho mobile)
    const handleOrientationChange = () => {
      // Delay một chút để đảm bảo window đã cập nhật
      setTimeout(updateDeviceInfo, 100);
    };

    window.addEventListener("orientationchange", handleOrientationChange);

    // Cleanup
    return () => {
      window.removeEventListener("resize", updateDeviceInfo);
      window.removeEventListener("orientationchange", handleOrientationChange);
    };
  }, [updateDeviceInfo]);

  return <DeviceContext.Provider value={deviceInfo}>{children}</DeviceContext.Provider>;
};

// Hook để sử dụng DeviceContext
export const useDevice = (): DeviceContextType => {
  const context = useContext(DeviceContext);

  if (context === undefined) {
    throw new Error("useDevice must be used within a DeviceProvider");
  }

  return context;
};
